import { PaginatedList } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { DetailCard, ManagementListToolbar, StatusPill } from "@taunggyi-plus/design-system";
import { queueInfo, serviceLabels } from "./dashboard-model";
type Threshold = { service: string; queue: string; minutes: number };
export function WorkThresholds() {
  const { client } = useAuth();
  const [rows, setRows] = useState<Threshold[]>([]);
  const [message, setMessage] = useFeedback();
  const [query, setQuery] = useState("");
  const [service, setService] = useState("all");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!client) return;
    const r = await client.rpc("admin_followup_status");
    if (r.error) {
      setMessage(r.error.message);
      return;
    }
    setRows((r.data as { thresholds: Threshold[] }).thresholds);
  }, [client, setMessage]);
  useEffect(() => {
    const timer = setTimeout(() => void load(), 0);
    return () => clearTimeout(timer);
  }, [load]);
  async function save(e: FormEvent<HTMLFormElement>, row: Threshold) {
    e.preventDefault();
    if (!client || busy) return;
    const minutes = Number(new FormData(e.currentTarget).get("minutes"));
    setBusy(true);
    const r = await client.rpc("admin_set_work_threshold", {
      target_service: row.service,
      target_queue: row.queue,
      target_minutes: minutes,
    });
    setBusy(false);
    setMessage(r.error?.message ?? "Overdue threshold saved.");
    if (!r.error) await load();
  }
  const filtered = rows.filter(
    (row) =>
      (service === "all" || row.service === service) &&
      `${serviceLabels[row.service]} ${queueInfo(row.service, row.queue)?.title}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
  );
  return (
    <div className="role-more-page">
      <a className="more-back-link" href="/more">
        ← More
      </a>

      {message && <p role="status">{message}</p>}
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search work thresholds"
        resultCount={filtered.length}
        filters={[
          {
            label: "Service",
            value: service,
            onChange: setService,
            options: [
              { value: "all", label: "All services" },
              ...Array.from(new Set(rows.map((row) => row.service))).map((value) => ({
                value,
                label: serviceLabels[value] ?? value,
              })),
            ],
          },
        ]}
        onClear={() => {
          setQuery("");
          setService("all");
        }}
      />
      {!filtered.length && <p>No thresholds match these filters.</p>}
      <PaginatedList
        items={filtered}
        label="Work thresholds"
        renderItem={(row) => (
          <DetailCard
            title={`${serviceLabels[row.service]} · ${queueInfo(row.service, row.queue)?.title ?? row.queue}`}
            summary={<StatusPill>Overdue after {row.minutes} minutes</StatusPill>}
            key={`${row.service}:${row.queue}:${row.minutes}`}
            className="management-card"
          >
            <h2>{serviceLabels[row.service]}</h2>
            <p>{queueInfo(row.service, row.queue)?.title}</p>
            <form className="form-grid" onSubmit={(e) => void save(e, row)}>
              <label className="field">
                <span>Overdue after (minutes)</span>
                <input
                  name="minutes"
                  type="number"
                  min="1"
                  max="10080"
                  step="1"
                  defaultValue={row.minutes}
                  required
                />
              </label>
              <button className="button" disabled={busy}>
                Save threshold
              </button>
            </form>
          </DetailCard>
        )}
      />
    </div>
  );
}
